import {
  FieldDefinition,
  FieldType,
  HighlightRule,
  LoanRecord,
  LoanTag,
  RawValue,
  RuleCondition,
  RuleMatch,
  RuleOperator,
} from '../types/highlights'

interface OperatorMeta {
  value: RuleOperator
  label: string
  inputs: 0 | 1 | 2
  /** Placeholder for the first value input. */
  placeholder?: string
}

const comparisonOperators: OperatorMeta[] = [
  { value: 'gt', label: 'is greater than', inputs: 1, placeholder: '0' },
  { value: 'gte', label: 'is greater or equal to', inputs: 1, placeholder: '0' },
  { value: 'lt', label: 'is less than', inputs: 1, placeholder: '0' },
  { value: 'lte', label: 'is less or equal to', inputs: 1, placeholder: '0' },
  { value: 'eq', label: 'equals', inputs: 1, placeholder: '0' },
  { value: 'neq', label: 'does not equal', inputs: 1, placeholder: '0' },
  { value: 'between', label: 'is between', inputs: 2, placeholder: '0' },
  { value: 'is_empty', label: 'is blank', inputs: 0 },
  { value: 'is_not_empty', label: 'is not blank', inputs: 0 },
]

const textOperators: OperatorMeta[] = [
  { value: 'eq', label: 'equals', inputs: 1, placeholder: 'Value' },
  { value: 'neq', label: 'does not equal', inputs: 1, placeholder: 'Value' },
  { value: 'contains', label: 'contains', inputs: 1, placeholder: 'Text' },
  { value: 'not_contains', label: 'does not contain', inputs: 1, placeholder: 'Text' },
  { value: 'is_empty', label: 'is blank', inputs: 0 },
  { value: 'is_not_empty', label: 'is not blank', inputs: 0 },
]

const dateOperators: OperatorMeta[] = [
  { value: 'within_days', label: 'is within (days)', inputs: 1, placeholder: '7' },
  { value: 'before', label: 'is before', inputs: 1, placeholder: 'M/D/YYYY' },
  { value: 'after', label: 'is after', inputs: 1, placeholder: 'M/D/YYYY' },
  { value: 'is_empty', label: 'is blank', inputs: 0 },
  { value: 'is_not_empty', label: 'is not blank', inputs: 0 },
]

const booleanOperators: OperatorMeta[] = [
  { value: 'is_true', label: 'is yes', inputs: 0 },
  { value: 'is_false', label: 'is no', inputs: 0 },
]

export const operatorsByType: Record<FieldType, OperatorMeta[]> = {
  number: comparisonOperators,
  text: textOperators,
  date: dateOperators,
  boolean: booleanOperators,
}

export function defaultOperatorFor(type: FieldType): RuleOperator {
  return operatorsByType[type][0].value
}

export function operatorMeta(
  type: FieldType,
  operator: RuleOperator,
): OperatorMeta | undefined {
  return operatorsByType[type].find((meta) => meta.value === operator)
}

function parseDate(value: RawValue): Date | null {
  if (typeof value !== 'string' || value.trim() === '') return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function daysUntil(value: RawValue): number | null {
  const date = parseDate(value)
  if (!date) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((date.getTime() - today.getTime()) / 86400000)
}

function isBlank(raw: RawValue): boolean {
  return raw === null || raw === undefined || raw === '' || raw === '—'
}

function matches(rule: RuleCondition, type: FieldType, raw: RawValue): boolean {
  const numeric = typeof raw === 'number' ? raw : Number(raw)
  const target = Number(rule.value)
  const target2 = Number(rule.value2)
  const text = typeof raw === 'string' ? raw.toLowerCase() : String(raw ?? '').toLowerCase()
  const compare = (rule.value ?? '').toLowerCase()

  switch (rule.operator) {
    case 'is_empty':
      return isBlank(raw)
    case 'is_not_empty':
      return !isBlank(raw)
    case 'is_true':
      return raw === true
    case 'is_false':
      return raw === false
    case 'contains':
      return compare !== '' && text.includes(compare)
    case 'not_contains':
      return compare !== '' && !text.includes(compare)
    case 'within_days': {
      const days = daysUntil(raw)
      return days !== null && Number.isFinite(target) && days <= target
    }
    case 'before': {
      const date = parseDate(raw)
      const other = parseDate(rule.value ?? null)
      return Boolean(date && other && date.getTime() < other.getTime())
    }
    case 'after': {
      const date = parseDate(raw)
      const other = parseDate(rule.value ?? null)
      return Boolean(date && other && date.getTime() > other.getTime())
    }
    default:
      break
  }

  if (type === 'text') {
    switch (rule.operator) {
      case 'eq':
        return text === compare
      case 'neq':
        return text !== compare
      default:
        return false
    }
  }

  if (isBlank(raw) || !Number.isFinite(numeric)) return false

  switch (rule.operator) {
    case 'gt':
      return numeric > target
    case 'gte':
      return numeric >= target
    case 'lt':
      return numeric < target
    case 'lte':
      return numeric <= target
    case 'eq':
      return numeric === target
    case 'neq':
      return numeric !== target
    case 'between':
      return (
        Number.isFinite(target) &&
        Number.isFinite(target2) &&
        numeric >= Math.min(target, target2) &&
        numeric <= Math.max(target, target2)
      )
    default:
      return false
  }
}

export function ruleMatches(
  rule: RuleCondition,
  loan: LoanRecord,
  byId: Map<string, FieldDefinition>,
): boolean {
  const source = byId.get(rule.sourceFieldId)
  if (!source) return false
  return matches(rule, source.type, source.getRaw(loan))
}

export function describeRule(
  rule: RuleCondition,
  byId: Map<string, FieldDefinition>,
): string {
  const source = byId.get(rule.sourceFieldId)
  if (!source) return 'Unknown field'
  const meta = operatorMeta(source.type, rule.operator)
  const label = meta?.label ?? rule.operator
  if (!meta || meta.inputs === 0) return `${source.label} ${label}`
  if (meta.inputs === 2) {
    return `${source.label} ${label} ${rule.value ?? '?'} and ${rule.value2 ?? '?'}`
  }
  return `${source.label} ${label} ${rule.value ?? '?'}`
}

/** First enabled matching rule for a field wins. */
export function evaluateField(
  fieldId: string,
  rules: HighlightRule[],
  loan: LoanRecord,
  byId: Map<string, FieldDefinition>,
): RuleMatch | null {
  for (const rule of rules) {
    if (!rule.enabled || rule.targetFieldId !== fieldId) continue
    if (!ruleMatches(rule, loan, byId)) continue
    return {
      tone: rule.tone,
      flag: rule.flag?.trim() ? rule.flag.trim() : undefined,
      reason: describeRule(rule, byId),
      ruleId: rule.id,
    }
  }
  return null
}

export function createRuleId(prefix = 'rule'): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`
}

/** Whether a tag should appear on this loan. Disabled tags never show. */
export function isTagVisible(
  tag: LoanTag,
  loan: LoanRecord,
  byId: Map<string, FieldDefinition>,
): boolean {
  if (!tag.enabled) return false
  if (tag.visibility === 'always') return true
  return ruleMatches(tag.condition, loan, byId)
}
